import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CalendarView = "month" | "week" | "day" | "agenda";
export type CalendarSource =
  | "bookings"
  | "custom"
  | "holidays"
  | "service"
  | "serviceEvents";
export type ServiceUtilizationFilter = "all" | "low" | "medium" | "high" | "overbooked";

const EMPTY_ENABLED_SOURCES: Record<CalendarSource, boolean> = {
  bookings: true,
  custom: true,
  holidays: true,
  service: true,
  serviceEvents: true,
};

function normalizeEnabledSources(
  patch: Partial<Record<CalendarSource, boolean>>
): Partial<Record<CalendarSource, boolean>> {
  const next: Partial<Record<CalendarSource, boolean>> = {};
  for (const [rawKey, enabled] of Object.entries(patch)) {
    if (typeof enabled !== "boolean") continue;
    next[rawKey as CalendarSource] = enabled;
  }
  return next;
}

/**
 * CalendarUIState
 *
 * Hinweis zu reloadSeq (Soft-Refetch-Trigger):
 * - Zweck: Sofortige Aktualisierung der Kalender-Eventdaten (TanStack Query),
 *   ohne dass die Seite neu gemountet oder der Fokus gewechselt werden muss.
 * - Funktionsweise: reloadSeq wird in den queryKey der Event-Fetch-Hooks
 *   (useBookings/useCustomEvents/usePublicHolidays/useVacationPeriods) einbezogen.
 *   Wenn eine Mutation (Create/Update/Delete/Cancel) erfolgreich war, wird
 *   bumpReloadSeq() dispatcht. Dadurch ändert sich der queryKey und React Query
 *   führt einen Refetch aus.
 * - Wann verwenden:
 *   • Bei Mutationen, die Kalenderdaten beeinflussen, aus onQueryStarted (RTK Query)
 *     nach Erfolg einmalig bumpReloadSeq() dispatchen.
 *   • Nicht für Bookings-Listenansicht notwendig, dort übernehmen RTK-Invalidations
 *     (LIST/IDs) und die per-Hook-Optionen (refetchOnMount/Focus/…)
 * - Falls Navigationsfluss immer ein router.back() ausführt und dadurch ein Mount
 *   entsteht, ist reloadSeq nicht zwingend – es dient primär als zukunftssichere
 *   Absicherung für in-place Änderungen ohne Routewechsel.
 */
export interface CalendarUIState {
  view: CalendarView;
  dateISO: string; // keep serializable
  enabledSources: Record<CalendarSource, boolean>;
  initialized: boolean;
  /**
   * Refetch-Trigger für Event-Queries (nur für Calendar-Events).
   * Wird in queryKeys genutzt, um Refetchs deterministisch auszulösen.
   */
  reloadSeq: number;
  serviceFilters: {
    offerId: number | null;
    trainer: string;
    utilization: ServiceUtilizationFilter;
  };
}

const initialState: CalendarUIState = {
  view: "month",
  dateISO: new Date().toISOString(),
  enabledSources: EMPTY_ENABLED_SOURCES,
  initialized: false,
  // Startet bei 0 – jede Mutation erhöht per bumpReloadSeq() um 1.
  reloadSeq: 0,
  serviceFilters: {
    offerId: null,
    trainer: "",
    utilization: "all",
  },
};

const calendarUiSlice = createSlice({
  name: "calendarUI",
  initialState,
  reducers: {
    setView(state, action: PayloadAction<CalendarView>) {
      state.view = action.payload;
    },
    setDateISO(state, action: PayloadAction<string>) {
      state.dateISO = action.payload;
    },
    setInitialized(state, action: PayloadAction<boolean>) {
      state.initialized = action.payload;
    },
    setEnabledSource(
      state,
      action: PayloadAction<{
        source: CalendarSource;
        enabled: boolean;
      }>
    ) {
      const { source, enabled } = action.payload;
      state.enabledSources[source] = enabled;
    },
    setEnabledSources(
      state,
      action: PayloadAction<Partial<Record<CalendarSource, boolean>>>
    ) {
      state.enabledSources = {
        ...state.enabledSources,
        ...normalizeEnabledSources(action.payload),
      };
    },
    /**
     * Erhöht den Refetch-Trigger um 1.
     * Typischer Aufrufort: onQueryStarted einer RTK-Query-Mutation nach success,
     * um sofortige Refetchs der Event-Queries auszulösen.
     */
    bumpReloadSeq(state) {
      state.reloadSeq += 1;
    },
    setServiceFilters(
      state,
      action: PayloadAction<Partial<CalendarUIState["serviceFilters"]>>
    ) {
      state.serviceFilters = { ...state.serviceFilters, ...action.payload };
    },
    resetServiceFilters(state) {
      state.serviceFilters = {
        offerId: null,
        trainer: "",
        utilization: "all",
      };
    },
  },
});

export const {
  setView,
  setDateISO,
  setInitialized,
  setEnabledSource,
  setEnabledSources,
  bumpReloadSeq,
  setServiceFilters,
  resetServiceFilters,
} = calendarUiSlice.actions;

export default calendarUiSlice.reducer;
